import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import type {
  LeaderboardEntry,
  LeaderboardPeriod,
  LeaderboardReward,
} from '@pons/contracts';
import {
  leaderboardPage,
  pageItems,
  periodDateRange,
  pointsLabel,
  rankLabel,
} from './model';
import { useLeaderboard } from './useLeaderboard';
import './leaderboard.css';

function Amount({ value }: { value: string }) {
  return (
    <>
      <span className="lb-points-full">{pointsLabel(value)}</span>
      <span className="lb-points-compact">{pointsLabel(value, true)}</span>
      <span className="lb-unit">PTS</span>
    </>
  );
}

function Reward({ value }: { value: LeaderboardReward }) {
  return (
    <>
      <span>{pointsLabel(value.points)} PTS</span>
      <span className="lb-reward-separator"> + </span>
      <span className="lb-reward-usdt">{pointsLabel(value.usdt)} USDT</span>
    </>
  );
}

function RankingRow({
  entry,
  weekly,
}: {
  entry: LeaderboardEntry;
  weekly: boolean;
}) {
  return (
    <div role="row" className={`lb-row${entry.you ? ' is-you' : ''}`}>
      <div
        role="cell"
        className={`lb-rank${entry.rank > 1000 ? ' is-rank-band' : ''}`}
        title={`Exact rank: ${entry.rank.toLocaleString('en-US')}`}
        aria-label={`Rank ${entry.rank.toLocaleString('en-US')}`}
      >
        {rankLabel(entry.rank)}
      </div>
      <div role="cell" className="lb-player">
        <span className="lb-avatar" aria-hidden="true">
          {entry.avatar}
        </span>
        <span className="lb-name" title={entry.name}>
          {entry.you ? 'You' : entry.name}
        </span>
      </div>
      <div role="cell" className="lb-metric">
        {entry.calls.toLocaleString('en-US')}
      </div>
      <div role="cell" className="lb-metric">
        {entry.winRate === null ? '—' : `${entry.winRate.toFixed(1)}%`}
      </div>
      <div
        role="cell"
        className="lb-amount"
        title={`${pointsLabel(entry.points)} PTS`}
      >
        <span>
          <Amount value={entry.points} />
        </span>
      </div>
      {weekly && (
        <div role="cell" className="lb-reward">
          {entry.reward ? (
            <span
              className="lb-reward-value"
              title={
                entry.reward.status === 'projected'
                  ? 'Projected reward — final results may change'
                  : entry.reward.status === 'paid'
                    ? 'USDT payout marked as paid'
                    : 'USDT payout pending'
              }
            >
              <Reward value={entry.reward} />
            </span>
          ) : (
            <span className="lb-reward-empty">No reward</span>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyRankingRow({ rank, weekly }: { rank: number; weekly: boolean }) {
  return (
    <div
      role="row"
      className="lb-row is-empty"
      aria-label={`Unoccupied rank ${rank}`}
    >
      <div role="cell" className="lb-rank">
        {rank.toLocaleString('en-US')}
      </div>
      <div role="cell" className="lb-player" />
      <div role="cell" className="lb-metric" />
      <div role="cell" className="lb-metric" />
      <div role="cell" className="lb-amount" />
      {weekly && <div role="cell" className="lb-reward" />}
    </div>
  );
}

function WeekPicker({
  periods,
  selectedId,
  onSelect,
}: {
  periods: LeaderboardPeriod[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const control = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const options = useRef<(HTMLButtonElement | null)[]>([]);
  const selected = periods.findIndex((period) => period.id === selectedId);
  const [line, setLine] = useState<number>();
  useLayoutEffect(() => {
    const option = options.current[selected];
    setLine(option ? option.offsetTop + option.offsetHeight - 4 : undefined);
  }, [selected, open, periods]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!control.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  const openAndFocus = () => {
    setOpen(true);
    requestAnimationFrame(() =>
      options.current[Math.max(0, selected)]?.focus(),
    );
  };
  function move(event: KeyboardEvent, index: number) {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      trigger.current?.focus();
      return;
    }
    const next =
      event.key === 'ArrowDown'
        ? (index + 1) % periods.length
        : event.key === 'ArrowUp'
          ? (index + periods.length - 1) % periods.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? periods.length - 1
              : -1;
    if (next >= 0) {
      event.preventDefault();
      options.current[next]?.focus();
    }
  }
  return (
    <div
      ref={control}
      role="group"
      aria-label="Week selection"
      className={`lb-week-picker${open ? ' is-open' : ''}`}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') setOpen(false);
      }}
    >
      <button
        ref={trigger}
        type="button"
        className="lb-history-trigger"
        disabled={!periods.length}
        aria-label="Choose week"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="leaderboard-weeks"
        onBlur={(event) => {
          if (!control.current?.contains(event.relatedTarget)) setOpen(false);
        }}
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse' && periods.length) setOpen(true);
        }}
        onClick={(event) => {
          if (event.detail === 0) openAndFocus();
          else setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
          }
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            openAndFocus();
          }
        }}
      >
        <ChevronDown aria-hidden="true" size={20} />
      </button>
      {open && (
        <div className="lb-period-menu" id="leaderboard-weeks">
          <div
            className={`lb-history-list${line !== undefined ? ' has-indicator' : ''}`}
            role="menu"
            aria-label="Weekly periods"
            style={{ '--lb-history-line-y': `${line ?? 0}px` } as CSSProperties}
          >
            {periods.map((period, index) => (
              <button
                type="button"
                key={period.id}
                ref={(node) => {
                  options.current[index] = node;
                }}
                className="lb-history-option"
                role="menuitemradio"
                aria-checked={period.id === selectedId}
                tabIndex={-1}
                onBlur={(event) => {
                  if (!control.current?.contains(event.relatedTarget))
                    setOpen(false);
                }}
                title={`${period.startsAt} – ${period.endsAt} (${period.timezone})`}
                onKeyDown={(event) => move(event, index)}
                onClick={() => {
                  onSelect(period.id);
                  setOpen(false);
                  trigger.current?.focus();
                }}
              >
                <span>Week {period.number}</span>
                <small>
                  {periodDateRange(
                    Date.parse(period.startsAt),
                    Date.parse(period.endsAt),
                    period.timezone,
                  )}
                </small>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function Leaderboard({ accountId }: { accountId?: string }) {
  const { selection, navigate, data, error, loading, retry } =
    useLeaderboard(accountId);
  const board = useRef<HTMLDivElement>(null);
  const weekly = selection.period === 'week';
  const [knownPeriods, setKnownPeriods] = useState<LeaderboardPeriod[]>([]);
  useEffect(() => {
    if (data) setKnownPeriods(data.periods);
  }, [data]);
  const periods = data?.periods ?? knownPeriods;
  const selectedId = data?.selectedPeriodId ?? selection.week ?? null;
  const selectedPeriod = periods.find((period) => period.id === selectedId);
  const view = data ? leaderboardPage(data, selection.page) : undefined;
  const pinned = view?.pinned;
  useLayoutEffect(() => {
    const root = board.current;
    if (!root) return;
    const align = () => {
      for (const kind of ['points', 'reward']) {
        const sample = root.querySelector<HTMLElement>(`.lb-reference-${kind}`);
        if (sample)
          root.style.setProperty(
            `--lb-${kind}-heading-width`,
            `${sample.getBoundingClientRect().width}px`,
          );
      }
    };
    align();
    let active = true;
    void document.fonts.ready.then(() => {
      if (active) align();
    });
    const observer = new ResizeObserver(align);
    observer.observe(root);
    return () => {
      active = false;
      observer.disconnect();
    };
  }, [weekly]);
  const page = view?.page ?? selection.page;
  const lastPage = view?.lastPage ?? Math.max(10, page);
  const selectPage = (next: number) => navigate({ ...selection, page: next });
  const empty =
    weekly && data && !data.configured
      ? 'Weekly leaderboard is not scheduled yet.'
      : weekly && selectedPeriod?.status === 'scheduled'
        ? `Week ${selectedPeriod.number} has not started yet.`
        : data && data.total === 0
          ? 'No leaderboard results yet.'
          : '';
  return (
    <section className="leaderboard-stage" aria-label="Leaderboard">
      <h1 className="sr-only">Leaderboard</h1>
      <div
        ref={board}
        className={`lb-board${weekly ? ' is-weekly' : ' is-all-time'}`}
      >
        <div className="lb-toolbar">
          <div className="lb-tabs" role="group" aria-label="Leaderboard period">
            <div className={`lb-period-control${weekly ? ' is-selected' : ''}`}>
              <button
                type="button"
                className="lb-tab"
                aria-pressed={weekly}
                onClick={() =>
                  navigate({ period: 'week', week: selection.week, page: 1 })
                }
              >
                <span className="lb-period-name">Weekly</span>
              </button>
              <WeekPicker
                periods={periods}
                selectedId={selectedId}
                onSelect={(week) => navigate({ period: 'week', week, page: 1 })}
              />
            </div>
            <button
              type="button"
              className="lb-tab lb-all-tab"
              aria-pressed={!weekly}
              onClick={() =>
                navigate({ period: 'all', week: selection.week, page: 1 })
              }
            >
              All time
            </button>
          </div>
        </div>
        <div
          className="lb-table"
          role="table"
          aria-label={
            weekly
              ? `Weekly leaderboard${selectedPeriod ? `, week ${selectedPeriod.number}` : ''}`
              : 'All time leaderboard'
          }
          aria-busy={loading}
        >
          <div className="lb-table-head" role="row">
            <span role="columnheader">Rank</span>
            <span role="columnheader">Nickname</span>
            <span role="columnheader" className="lb-optional">
              Calls
            </span>
            <span role="columnheader" className="lb-optional">
              Win rate
            </span>
            <span role="columnheader" className="lb-points-column">
              <span className="lb-points-heading">Points</span>
              <span
                aria-hidden="true"
                className="lb-heading-reference lb-reference-points"
              >
                <Amount value="132128" />
              </span>
            </span>
            {weekly && (
              <span role="columnheader" className="lb-reward-column">
                <span className="lb-reward-heading">Reward</span>
                <span
                  aria-hidden="true"
                  className="lb-heading-reference lb-reference-reward"
                >
                  <Reward
                    value={{
                      points: '90000',
                      usdt: '900',
                      status: 'projected',
                    }}
                  />
                </span>
              </span>
            )}
          </div>
          <div className="lb-ranking" role="rowgroup">
            {pinned && (
              <RankingRow
                key={`self-${pinned.id}`}
                entry={pinned}
                weekly={weekly}
              />
            )}
            {view?.slots.map(({ rank, entry }) =>
              entry ? (
                <RankingRow key={rank} entry={entry} weekly={weekly} />
              ) : (
                <EmptyRankingRow key={rank} rank={rank} weekly={weekly} />
              ),
            )}
            {(loading || empty || error) && (
              <div
                className={`lb-status${view ? ' has-rows' : ''}`}
                role={error ? 'alert' : 'status'}
              >
                {error || (loading ? 'Loading leaderboard…' : empty)}
                {error && (
                  <button type="button" onClick={retry}>
                    Try again
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <nav className="lb-pagination" aria-label="Leaderboard pages">
          <button
            type="button"
            className="lb-page-button"
            aria-label="Previous page"
            disabled={!data || page <= 1}
            onClick={() => selectPage(page - 1)}
          >
            <ChevronLeft aria-hidden="true" size={14} />
          </button>
          {pageItems(page, lastPage).map((item, index) =>
            item === 'ellipsis' ? (
              <span
                className="lb-pagination-ellipsis"
                key={`gap-${index}`}
                aria-hidden="true"
              >
                …
              </span>
            ) : (
              <button
                type="button"
                className={`lb-page-button${page > 3 && page < lastPage - 2 && item !== page && item !== 1 && item !== lastPage ? ' is-neighbor' : ''}`}
                key={item}
                aria-label={
                  item === lastPage && lastPage > 1
                    ? `Last page, ${item.toLocaleString('en-US')}`
                    : `Page ${item.toLocaleString('en-US')}`
                }
                aria-current={item === page ? 'page' : undefined}
                disabled={!data}
                onClick={() => selectPage(item)}
              >
                {item.toLocaleString('en-US')}
              </button>
            ),
          )}
          <button
            type="button"
            className="lb-page-button"
            aria-label="Next page"
            disabled={!data || page >= lastPage}
            onClick={() => selectPage(page + 1)}
          >
            <ChevronRight aria-hidden="true" size={14} />
          </button>
        </nav>
      </div>
    </section>
  );
}
